import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Smile, Eye, CheckCircle2, ArrowRight, DollarSign, Building2, Star } from 'lucide-react';
import { Button } from '../components/ui/button';

const VISION_IMAGE = "https://images.unsplash.com/photo-1758599543111-36ce5c34fceb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzV8MHwxfHNlYXJjaHwxfHx3b21hbiUyMHNtaWxpbmclMjB3ZWFyaW5nJTIwZ2xhc3NlcyUyMHBvcnRyYWl0fGVufDB8fHx8MTc3MDY3Nzc5M3ww&ixlib=rb-4.1.0&q=85";
const DENTAL_IMAGE = "https://images.unsplash.com/photo-1607946037277-c9c31251b395?crop=entropy&cs=srgb&fm=jpg&q=85&w=800";

const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.5 }
};

export const DentalVisionPage = () => {
  const { t } = useTranslation();
  const dentalPlans = t('dentalVision.plans', { returnObjects: true });
  const visionBenefits = t('dentalVision.visionBenefits', { returnObjects: true });
  const dentalServices = t('dentalVision.services', { returnObjects: true });

  return (
    <div className="overflow-hidden" data-testid="dental-vision-page">
      {/* Hero Section */}
      <section className="gradient-hero py-20 lg:py-32" data-testid="dv-hero">
        <div className="container-custom">
          <motion.div initial="initial" animate="animate" variants={fadeInUp} className="max-w-3xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-sky-50 text-sky-600 rounded-full text-sm font-medium mb-6">
              <Smile className="h-4 w-4" />
              {t('dentalVision.heroBadge')}
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 tracking-tight leading-tight mb-6">
              {t('dentalVision.heroTitle')} <span className="text-gradient">{t('dentalVision.heroHighlight')}</span>
            </h1>
            <p className="text-lg text-slate-600 mb-8">{t('dentalVision.heroDesc')}</p>
            <Link to="/contact">
              <Button className="btn-accent" data-testid="dv-hero-cta">
                {t('dentalVision.heroCta')}
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Dental Section */}
      <section className="section-spacing bg-white" data-testid="dv-dental">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
              <img src={DENTAL_IMAGE} alt="Young couple smiling with healthy teeth" className="rounded-2xl shadow-xl" data-testid="dental-image" />
            </motion.div>
            <motion.div initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-sky-50 text-sky-600 rounded-full text-sm font-medium mb-6">
                <Smile className="h-4 w-4" />
                {t('dentalVision.dentalBadge')}
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">{t('dentalVision.dentalTitle')}</h2>
              <p className="text-lg text-slate-600 mb-8">{t('dentalVision.dentalText')}</p>
              <div className="space-y-4">
                {dentalPlans.map((plan, index) => (
                  <div key={plan.name} className="bg-slate-50 p-6 rounded-xl" data-testid={`dental-plan-${index}`}>
                    <div className="flex items-center gap-3 mb-3">
                      <div className="h-10 w-10 bg-sky-500 rounded-lg flex items-center justify-center">
                        <Building2 className="h-5 w-5 text-white" />
                      </div>
                      <h3 className="text-xl font-semibold text-slate-900">{plan.name}</h3>
                    </div>
                    <p className="text-slate-600 mb-4">{plan.description}</p>
                    <ul className="space-y-2">
                      {plan.features.map((feature) => (
                        <li key={feature} className="flex items-center gap-2">
                          <CheckCircle2 className="h-4 w-4 text-sky-500" />
                          <span className="text-slate-700 text-sm">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Dental Coverage Breakdown */}
      <section className="section-spacing gradient-subtle" data-testid="dv-coverage">
        <div className="container-custom">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">{t('dentalVision.coverageTitle')}</h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">{t('dentalVision.coverageSubtitle')}</p>
          </motion.div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {dentalServices.map((item, index) => (
              <motion.div key={item.service} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: index * 0.1 }} className="bg-white p-6 rounded-2xl border border-slate-100 card-shadow" data-testid={`coverage-card-${index}`}>
                <h3 className="text-lg font-semibold text-slate-900 mb-2">{item.service}</h3>
                <p className="text-slate-600 text-sm mb-4">{item.examples}</p>
                <div className="inline-flex items-center gap-2 px-3 py-1 bg-sky-50 text-sky-600 rounded-full text-sm font-medium">
                  <DollarSign className="h-3 w-3" />
                  {item.coverage}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Vision Section */}
      <section className="section-spacing bg-white" data-testid="dv-vision">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} className="order-2 lg:order-1">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-violet-50 text-violet-600 rounded-full text-sm font-medium mb-6">
                <Eye className="h-4 w-4" />
                {t('dentalVision.visionBadge')}
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">{t('dentalVision.visionTitle')}</h2>
              <p className="text-lg text-slate-600 mb-8">{t('dentalVision.visionText')}</p>
              <div className="grid grid-cols-2 gap-4 mb-8">
                {visionBenefits.map((benefit) => (
                  <div key={benefit} className="flex items-center gap-2">
                    <CheckCircle2 className="h-5 w-5 text-violet-500 flex-shrink-0" />
                    <span className="text-slate-700 text-sm">{benefit}</span>
                  </div>
                ))}
              </div>
              <div className="bg-violet-50 p-6 rounded-xl">
                <div className="flex items-start gap-4">
                  <Star className="h-6 w-6 text-violet-500 flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-2">{t('dentalVision.visionCardTitle')}</h4>
                    <p className="text-slate-600 text-sm">{t('dentalVision.visionCardText')}</p>
                  </div>
                </div>
              </div>
            </motion.div>
            <motion.div initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} className="order-1 lg:order-2">
              <img src={VISION_IMAGE} alt="Woman wearing glasses with healthy vision" className="rounded-2xl shadow-xl" data-testid="vision-image" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Affordability Section */}
      <section className="section-spacing gradient-subtle" data-testid="dv-affordability">
        <div className="container-custom text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <DollarSign className="h-12 w-12 text-sky-500 mx-auto mb-6" />
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">{t('dentalVision.affordTitle')}</h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto mb-8">{t('dentalVision.affordText')}</p>
            <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto mb-12">
              <div className="bg-white p-6 rounded-xl border border-slate-100 card-shadow">
                <h4 className="text-3xl font-bold text-sky-500 mb-2">$15-30</h4>
                <p className="text-slate-600">{t('dentalVision.affordDental')}</p>
              </div>
              <div className="bg-white p-6 rounded-xl border border-slate-100 card-shadow">
                <h4 className="text-3xl font-bold text-sky-500 mb-2">$5-15</h4>
                <p className="text-slate-600">{t('dentalVision.affordVision')}</p>
              </div>
              <div className="bg-white p-6 rounded-xl border border-slate-100 card-shadow">
                <h4 className="text-3xl font-bold text-sky-500 mb-2">100%</h4>
                <p className="text-slate-600">{t('dentalVision.affordPreventive')}</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-slate-900" data-testid="dv-cta">
        <div className="container-custom text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">{t('dentalVision.ctaTitle')}</h2>
            <p className="text-slate-300 text-lg mb-8 max-w-2xl mx-auto">{t('dentalVision.ctaText')}</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact">
                <Button className="bg-sky-500 hover:bg-sky-600 text-white h-12 px-8 rounded-full font-semibold" data-testid="dv-cta-btn">
                  {t('dentalVision.ctaBtn')}
                </Button>
              </Link>
              <Link to="/health-plans">
                <Button variant="outline" className="border-white/20 text-white hover:bg-white/10 h-12 px-8 rounded-full" data-testid="dv-health-btn">
                  {t('dentalVision.ctaHealthBtn')}
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default DentalVisionPage;
